import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Download, FileText, Image, Plus, Trash2,
  ChevronUp, ChevronDown, Copy, Search, Loader2, GripVertical,
  Upload, Sparkles, Check, Cloud, CloudOff, Wifi, X,
  MapPin, Hotel, Utensils, Calendar, Users, Star,
  RefreshCw, History, RotateCcw, Mail, Send, LayoutTemplate,
  MessageSquare, Bot, ChevronRight, Link2
} from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const PAGE_TYPES = {
  cover: { label: 'Cover', icon: Star, gradient: 'from-blue-600 to-indigo-700' },
  day: { label: 'Day', icon: Calendar, gradient: 'from-sky-500 to-blue-600' },
  hotels: { label: 'Hotels', icon: Hotel, gradient: 'from-violet-500 to-purple-600' },
  inclusions: { label: 'Inc/Exc', icon: Check, gradient: 'from-emerald-500 to-green-600' },
  pricing: { label: 'Pricing', icon: Star, gradient: 'from-orange-500 to-amber-600' },
  terms: { label: 'Terms', icon: FileText, gradient: 'from-slate-500 to-gray-600' },
};

// Auto-fetch image for a page based on its content
async function fetchPageImage(page) {
  let query = '';
  if (page.type === 'cover') query = page.destination || page.title || 'travel destination';
  else if (page.type === 'day') query = (page.activities?.[0] || page.title || 'travel sightseeing') + ' destination';
  else return null;
  if (!query.trim()) return null;
  try {
    const res = await api.get(`/builder/images/search?query=${encodeURIComponent(query)}&per_page=1`);
    const results = res.data?.results;
    if (results?.length) return results[0].url_regular;
  } catch {}
  return null;
}

// Sortable page item for drag-and-drop
function SortablePageItem({ page, index, selected, onClick, PT }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: page.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const Icon = PT.icon;
  return (
    <div ref={setNodeRef} style={style} {...attributes}
      className={`w-full text-left px-3 py-2.5 rounded-lg text-xs transition-all group flex items-center gap-2 cursor-pointer ${
        selected ? 'bg-gradient-to-r ' + PT.gradient + ' text-white shadow-sm' : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400'
      }`}
      onClick={onClick} data-testid={`page-thumb-${index}`}>
      <div {...listeners} className="cursor-grab active:cursor-grabbing"><GripVertical className="w-3.5 h-3.5 opacity-40 group-hover:opacity-100" /></div>
      <Icon className="w-3.5 h-3.5 flex-shrink-0" />
      <span className="truncate font-medium">{page.type === 'day' ? `Day ${page.day_number || index}` : PT.label}: {page.title?.substring(0, 18)}</span>
      {page.image_url && <Image className="w-3 h-3 ml-auto opacity-40" />}
    </div>
  );
}

export default function ItineraryEditor() {
  const { id: itineraryId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveState, setSaveState] = useState('idle');
  const [exporting, setExporting] = useState(false);
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(0);
  const [itinerary, setItinerary] = useState(null);
  const [branding, setBranding] = useState({});
  const [lead, setLead] = useState(null);
  const [showImageSearch, setShowImageSearch] = useState(false);
  const [imageTarget, setImageTarget] = useState(null);
  const [imageResults, setImageResults] = useState([]);
  const [imageQuery, setImageQuery] = useState('');
  const [searchingImages, setSearchingImages] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [showUploadDoc, setShowUploadDoc] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [regenerating, setRegenerating] = useState(null);
  const [showRevisions, setShowRevisions] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [showEmail, setShowEmail] = useState(false);
  const [emailData, setEmailData] = useState({ to_email: '', subject: '', message: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [hotelImageTarget, setHotelImageTarget] = useState(null); // {pageIdx, hotelIdx}
  const [showAgent, setShowAgent] = useState(false);
  const [agentMessages, setAgentMessages] = useState([{ role: 'assistant', text: 'Hi! I\'m your AI editor. I can do anything you need:\n\n🖼 "Change the cover image to a sunset beach"\n✍️ "Rewrite Day 2 in a luxury romantic tone"\n🏨 "Add hotel photo for Ritz Carlton"\n📝 "Add a spa & wellness day after Day 3"\n🗑 "Remove Day 5"\n🎨 "Generate an AI image of cherry blossoms in Tokyo"\n\nJust tell me what you want!' }]);
  const [agentInput, setAgentInput] = useState('');
  const [agentProcessing, setAgentProcessing] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const chatEndRef = useRef(null);
  const autoSaveRef = useRef(null);
  const pagesRef = useRef(pages);
  pagesRef.current = pages;

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => { loadEditorData(); }, [itineraryId]);

  const loadEditorData = async () => {
    try {
      const res = await api.get(`/builder/itinerary/${itineraryId}/editor-data`);
      const loadedPages = res.data.pages || [];
      setPages(loadedPages);
      setItinerary(res.data.itinerary);
      setBranding(res.data.branding || {});
      setLead(res.data.lead);
      // Auto-fetch images for pages that don't have them
      autoFetchImages(loadedPages);
    } catch (err) {
      toast.error('Failed to load itinerary');
      navigate(-1);
    } finally { setLoading(false); }
  };

  const autoFetchImages = async (pageList) => {
    const updates = [];
    for (let i = 0; i < pageList.length; i++) {
      const p = pageList[i];
      if ((p.type === 'cover' || p.type === 'day') && !p.image_url) {
        updates.push({ index: i, page: p });
      }
    }
    // Fetch in parallel batches of 4
    for (let batch = 0; batch < updates.length; batch += 4) {
      const chunk = updates.slice(batch, batch + 4);
      const results = await Promise.all(chunk.map(u => fetchPageImage(u.page)));
      setPages(prev => {
        const copy = [...prev];
        chunk.forEach((u, j) => {
          if (results[j] && copy[u.index]) {
            copy[u.index] = { ...copy[u.index], image_url: results[j] };
          }
        });
        return copy;
      });
    }
  };

  // Autosave
  const triggerAutosave = useCallback(() => {
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    setSaveState('unsaved');
    autoSaveRef.current = setTimeout(async () => {
      setSaveState('saving');
      try {
        await api.put(`/builder/itinerary/${itineraryId}/pages`, { pages: pagesRef.current });
        setSaveState('saved');
        setTimeout(() => setSaveState(s => s === 'saved' ? 'idle' : s), 3000);
      } catch { setSaveState('error'); }
    }, 2000);
  }, [itineraryId]);

  useEffect(() => {
    if (pages.length > 0 && !loading) triggerAutosave();
    return () => { if (autoSaveRef.current) clearTimeout(autoSaveRef.current); };
  }, [pages, triggerAutosave, loading]);

  const updatePage = (index, field, value) => {
    setPages(prev => { const u = [...prev]; u[index] = { ...u[index], [field]: value }; return u; });
  };

  const updatePageNested = (index, field, subIndex, value) => {
    setPages(prev => { const u = [...prev]; const arr = [...(u[index][field] || [])]; arr[subIndex] = value; u[index] = { ...u[index], [field]: arr }; return u; });
  };

  const movePage = (i, d) => {
    const n = i + d;
    if (n < 0 || n >= pages.length) return;
    setPages(prev => { const u = [...prev]; [u[i], u[n]] = [u[n], u[i]]; return u; });
    setSelectedPage(n);
  };

  const deletePage = (i) => { if (pages.length <= 1) return; setPages(p => p.filter((_, j) => j !== i)); setSelectedPage(Math.min(selectedPage, pages.length - 2)); };
  const duplicatePage = (i) => { const c = { ...pages[i], id: `page-${Date.now()}` }; setPages(p => [...p.slice(0, i + 1), c, ...p.slice(i + 1)]); };

  const addPage = (type) => {
    const templates = {
      day: { type: 'day', day_number: pages.filter(p => p.type === 'day').length + 1, title: 'New Day', description: '', activities: [], hotel: null, meals: null, image_url: null },
      hotels: { type: 'hotels', title: 'Hotel Accommodation', hotels: [] },
      inclusions: { type: 'inclusions', title: 'Inclusions & Exclusions', inclusions: [], exclusions: [] },
      pricing: { type: 'pricing', title: 'Package Pricing', costing: '' },
      terms: { type: 'terms', title: 'Terms & Conditions', terms: [], notes: '' },
    };
    const np = { id: `page-${Date.now()}`, ...(templates[type] || templates.day) };
    setPages(p => [...p, np]);
    setSelectedPage(pages.length);
  };

  const handleSave = async () => {
    setSaving(true); setSaveState('saving');
    try {
      await api.put(`/builder/itinerary/${itineraryId}/pages`, { pages });
      setSaveState('saved');
      toast.success('Saved!');
    } catch { toast.error('Save failed'); setSaveState('error'); }
    finally { setSaving(false); }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      await api.put(`/builder/itinerary/${itineraryId}/pages`, { pages });
      const res = await api.get(`/builder/itinerary/${itineraryId}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `${itinerary?.title || 'Itinerary'}.pdf`; a.click(); URL.revokeObjectURL(url);
      toast.success('PDF exported!');
    } catch { toast.error('PDF export failed'); }
    finally { setExporting(false); }
  };

  const handleExportWord = async () => {
    try {
      const res = await api.get(`/itinerary/${itineraryId}/download-docx`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data])); const a = document.createElement('a'); a.href = url; a.download = `${itinerary?.title || 'Itinerary'}.docx`; a.click(); URL.revokeObjectURL(url);
      toast.success('Word doc exported!');
    } catch { toast.error('Word export failed'); }
  };

  // Image search
  const searchImages = async () => {
    if (!imageQuery.trim()) return;
    setSearchingImages(true);
    try { const res = await api.get(`/builder/images/search?query=${encodeURIComponent(imageQuery)}&per_page=12`); setImageResults(res.data.results || []); }
    catch { toast.error('Image search failed'); }
    finally { setSearchingImages(false); }
  };

  const generateAIImage = async () => {
    if (!imageQuery.trim()) return;
    setAiGenerating(true);
    try {
      const res = await api.post('/builder/images/generate', { prompt: `Professional travel photography: ${imageQuery}. Beautiful, vibrant, high quality.` });
      if (res.data.image_base64) setImageResults(prev => [{ id: 'ai-gen-' + Date.now(), url_regular: `data:image/png;base64,${res.data.image_base64}`, url_small: `data:image/png;base64,${res.data.image_base64}`, alt: imageQuery, source: 'ai', photographer: 'AI Generated' }, ...prev]);
    } catch { toast.error('AI image generation failed'); }
    finally { setAiGenerating(false); }
  };

  const openImageSearch = (idx, q) => { setImageTarget(idx); setHotelImageTarget(null); setImageQuery(q || ''); setImageResults([]); setShowImageSearch(true); };

  // Document upload
  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await api.post('/builder/upload-document', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const sd = res.data.structured_data;
      if (sd && !sd.error) {
        toast.success(`Extracted: ${sd.destination || 'Trip data'} with ${sd.hotels?.length || 0} hotels`);
      }
      setShowUploadDoc(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploadingDoc(false); }
  };

  // Per-page AI regeneration
  const regeneratePage = async (idx) => {
    setRegenerating(idx);
    try {
      const res = await api.post(`/builder/itinerary/${itineraryId}/regenerate-page`, { page_index: idx, tone: 'professional' });
      if (res.data.page) {
        setPages(prev => { const u = [...prev]; u[idx] = res.data.page; return u; });
        toast.success('Page regenerated!');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Regeneration failed'); }
    finally { setRegenerating(null); }
  };

  // Revision history
  const loadRevisions = async () => {
    try {
      const res = await api.get(`/builder/itinerary/${itineraryId}/revisions`);
      setRevisions(res.data || []);
      setShowRevisions(true);
    } catch { toast.error('Failed to load revisions'); }
  };

  const restoreRevision = async (revId) => {
    try {
      await api.post(`/builder/itinerary/${itineraryId}/revisions/${revId}/restore`);
      toast.success('Revision restored!');
      setShowRevisions(false);
      loadEditorData();
    } catch { toast.error('Failed to restore'); }
  };

  // Drag and drop handler
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setPages(prev => {
      const oldIdx = prev.findIndex(p => p.id === active.id);
      const newIdx = prev.findIndex(p => p.id === over.id);
      return arrayMove(prev, oldIdx, newIdx);
    });
  };

  // Email itinerary
  const handleSendEmail = async () => {
    if (!emailData.to_email.trim()) { toast.error('Enter recipient email'); return; }
    setSendingEmail(true);
    try {
      await api.put(`/builder/itinerary/${itineraryId}/pages`, { pages });
      const res = await api.post(`/builder/itinerary/${itineraryId}/email`, emailData);
      toast.success(res.data.message || 'Itinerary emailed!');
      setShowEmail(false);
      setEmailData({ to_email: '', subject: '', message: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Email failed'); }
    finally { setSendingEmail(false); }
  };

  // Hotel image search handler
  const openHotelImageSearch = (hotelIdx) => {
    const hotel = (pages[selectedPage]?.hotels || [])[hotelIdx];
    const query = hotel ? `${hotel.hotel_name || hotel.name || ''} ${hotel.place || hotel.city || ''} hotel resort`.trim() : 'luxury hotel resort';
    setHotelImageTarget({ pageIdx: selectedPage, hotelIdx });
    setImageTarget(null);
    setImageQuery(query);
    setImageResults([]);
    setShowImageSearch(true);
  };

  const selectImage = (img) => {
    if (hotelImageTarget) {
      // Update hotel image
      const { pageIdx, hotelIdx } = hotelImageTarget;
      setPages(prev => {
        const u = [...prev];
        const hotels = [...(u[pageIdx].hotels || [])];
        hotels[hotelIdx] = { ...hotels[hotelIdx], image_url: img.url_regular };
        u[pageIdx] = { ...u[pageIdx], hotels };
        return u;
      });
      setHotelImageTarget(null);
    } else if (imageTarget !== null) {
      updatePage(imageTarget, 'image_url', img.url_regular);
    }
    setShowImageSearch(false);
  };

  // AI Agent Chat
  const sendAgentMessage = async () => {
    if (!agentInput.trim() || agentProcessing) return;
    const userMsg = agentInput.trim();
    setAgentInput('');
    setAgentMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setAgentProcessing(true);
    setAgentMessages(prev => [...prev, { role: 'status', text: 'Analyzing your request...' }]);
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    try {
      const res = await api.post(`/builder/itinerary/${itineraryId}/agent-chat`, { message: userMsg, page_index: selectedPage });
      // Remove the status message
      setAgentMessages(prev => prev.filter(m => m.role !== 'status'));
      const changes = res.data.changes_count || 0;
      let replyText = res.data.reply;
      if (changes > 0) {
        replyText += `\n\n✅ ${changes} change${changes > 1 ? 's' : ''} applied`;
      }
      setAgentMessages(prev => [...prev, { role: 'assistant', text: replyText }]);
      if (res.data.pages) {
        setPages(res.data.pages);
        toast.success(`${changes} change(s) applied by AI`);
      }
    } catch (err) {
      setAgentMessages(prev => prev.filter(m => m.role !== 'status'));
      setAgentMessages(prev => [...prev, { role: 'assistant', text: 'Sorry, something went wrong. Please try again.' }]);
    } finally {
      setAgentProcessing(false);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  };

  // Share link
  const generateShareLink = async () => {
    try {
      const res = await api.post(`/builder/itinerary/${itineraryId}/share`);
      const token = res.data.share_token;
      const url = `${window.location.origin}/shared/${token}`;
      setShareLink(url);
      navigator.clipboard.writeText(url);
      toast.success('Share link copied to clipboard!');
    } catch { toast.error('Failed to generate share link'); }
  };

  // WhatsApp share
  const shareViaWhatsApp = async () => {
    try {
      const res = await api.post(`/builder/itinerary/${itineraryId}/whatsapp-link`);
      const token = res.data.share_token;
      const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/builder/shared/${token}/pdf`;
      const msg = res.data.message_template.replace('{pdf_url}', pdfUrl);
      const phone = (res.data.phone || '').replace(/[^0-9+]/g, '');
      const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
      window.open(waUrl, '_blank');
    } catch { toast.error('Failed to create WhatsApp link'); }
  };

  const page = pages[selectedPage];

  if (loading) return (
    <div className="flex items-center justify-center h-screen bg-background">
      <Loader2 className="w-8 h-8 text-primary animate-spin" />
    </div>
  );

  const SaveIndicator = () => {
    if (saveState === 'saving') return <span className="flex items-center gap-1.5 text-xs text-amber-500 animate-pulse"><Loader2 className="w-3 h-3 animate-spin" />Saving...</span>;
    if (saveState === 'saved') return <span className="flex items-center gap-1.5 text-xs text-emerald-500"><Cloud className="w-3 h-3" />Saved</span>;
    if (saveState === 'error') return <span className="flex items-center gap-1.5 text-xs text-red-500"><CloudOff className="w-3 h-3" />Error</span>;
    if (saveState === 'unsaved') return <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Wifi className="w-3 h-3" />Unsaved</span>;
    return <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Cloud className="w-3 h-3" />Up to date</span>;
  };

  const primary = branding.colors?.primary || '#0E4F8F';

  return (
    <div className="flex h-screen bg-slate-50 dark:bg-slate-950" data-testid="itinerary-editor">
      {/* Left Sidebar */}
      <div className="w-60 bg-white dark:bg-slate-900 border-r border-border flex flex-col shadow-sm">
        <div className="p-3 border-b border-border">
          <Button variant="ghost" size="sm" className="gap-2 mb-2 w-full justify-start text-muted-foreground hover:text-foreground" onClick={() => navigate(-1)} data-testid="editor-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back to Lead
          </Button>
          <h2 className="font-heading text-sm font-bold truncate px-1">{itinerary?.title || 'Itinerary'}</h2>
          <div className="px-1 mt-1"><SaveIndicator /></div>
        </div>

        <ScrollArea className="flex-1 py-2">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={pages.map(p => p.id)} strategy={verticalListSortingStrategy}>
              <div className="px-2 space-y-0.5">
                {pages.map((p, i) => {
                  const PT = PAGE_TYPES[p.type] || PAGE_TYPES.day;
                  return <SortablePageItem key={p.id} page={p} index={i} selected={i === selectedPage}
                    onClick={() => setSelectedPage(i)} PT={PT} />;
                })}
              </div>
            </SortableContext>
          </DndContext>
        </ScrollArea>

        <div className="p-2 border-t border-border">
          <p className="text-[10px] text-muted-foreground px-2 mb-1.5 font-medium uppercase tracking-wide">Add Page</p>
          <div className="grid grid-cols-2 gap-1">
            {Object.entries(PAGE_TYPES).filter(([k]) => k !== 'cover').map(([key, val]) => {
              const Icon = val.icon;
              return (
                <Button key={key} variant="ghost" size="sm" className="justify-start text-[10px] gap-1.5 h-7 px-2" onClick={() => addPage(key)} data-testid={`add-page-${key}`}>
                  <Icon className="w-3 h-3" /> {val.label}
                </Button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Center Canvas */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Toolbar */}
        <div className="flex items-center gap-1.5 px-4 py-2 bg-white dark:bg-slate-900 border-b border-border shadow-sm">
          <div className="flex items-center gap-0.5 bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5">
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => movePage(selectedPage, -1)} disabled={selectedPage === 0}><ChevronUp className="w-3.5 h-3.5" /></Button>
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => movePage(selectedPage, 1)} disabled={selectedPage === pages.length - 1}><ChevronDown className="w-3.5 h-3.5" /></Button>
          </div>
          <Button variant="ghost" size="sm" className="gap-1 h-7 text-xs" onClick={() => duplicatePage(selectedPage)}><Copy className="w-3.5 h-3.5" /> Clone</Button>
          <Button variant="ghost" size="sm" className="gap-1 h-7 text-xs text-red-500 hover:text-red-700" onClick={() => deletePage(selectedPage)} disabled={pages.length <= 1}><Trash2 className="w-3.5 h-3.5" /></Button>
          <div className="w-px h-5 bg-border mx-1" />
          <Button variant="ghost" size="sm" className="gap-1 h-7 text-xs" onClick={() => setShowUploadDoc(true)} data-testid="upload-doc-btn"><Upload className="w-3.5 h-3.5" /> Import</Button>
          <Button variant="ghost" size="sm" className="gap-1 h-7 text-xs text-violet-600" onClick={() => regeneratePage(selectedPage)} disabled={regenerating !== null} data-testid="regenerate-page-btn">
            {regenerating === selectedPage ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} AI Rewrite
          </Button>
          <Button variant="ghost" size="sm" className="gap-1 h-7 text-xs" onClick={loadRevisions} data-testid="history-btn"><History className="w-3.5 h-3.5" /> History</Button>
          <Button variant="ghost" size="sm" className="gap-1 h-7 text-xs" onClick={generateShareLink} data-testid="share-btn"><Link2 className="w-3.5 h-3.5" /> Share</Button>
          <Button variant="ghost" size="sm" className="gap-1 h-7 text-xs text-green-600" onClick={shareViaWhatsApp} data-testid="whatsapp-btn">
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492l4.634-1.215A11.95 11.95 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75c-2.115 0-4.142-.665-5.831-1.885l-.418-.271-2.743.72.733-2.677-.298-.443A9.708 9.708 0 012.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75z"/></svg>
            WhatsApp
          </Button>
          <div className="flex-1" />
          <SaveIndicator />
          <div className="w-px h-5 bg-border mx-1" />
          <Button variant="outline" size="sm" className="gap-1.5 h-8" onClick={handleSave} disabled={saving} data-testid="save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5 h-8" onClick={handleExportWord}><FileText className="w-3.5 h-3.5" /> Word</Button>
          <Button variant="outline" size="sm" className="gap-1.5 h-8 text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={() => setShowEmail(true)} data-testid="email-btn">
            <Mail className="w-3.5 h-3.5" /> Email
          </Button>
          <Button size="sm" className="gap-1.5 h-8 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-sm" onClick={handleExportPDF} disabled={exporting} data-testid="export-pdf-btn">
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} PDF
          </Button>
        </div>

        {/* Page Canvas */}
        <ScrollArea className="flex-1 bg-slate-100/50 dark:bg-slate-950/50">
          <div className="p-8">
            {page && (
              <div className="max-w-[820px] mx-auto bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 overflow-hidden" data-testid="page-canvas">
                {/* Header with logo */}
                {page.type !== 'cover' && (
                  <div className="flex items-center gap-3 px-8 py-3 border-b-2" style={{ borderColor: primary }}>
                    {branding.logo_url ? <img src={branding.logo_url} alt="Logo" className="h-9 max-w-[130px] object-contain" /> : <div className="font-heading font-bold text-sm" style={{ color: primary }}>{branding.company_name}</div>}
                    <span className="text-[10px] text-slate-400 ml-auto">{branding.company_name}</span>
                  </div>
                )}

                {/* ── COVER ─────────────────── */}
                {page.type === 'cover' && (
                  <div className="relative">
                    {/* Hero Image */}
                    <div className="relative h-72 bg-gradient-to-br from-blue-900 to-indigo-900 overflow-hidden cursor-pointer group" onClick={() => openImageSearch(selectedPage, page.destination || page.title)}>
                      {page.image_url ? (
                        <img src={page.image_url} alt="" className="w-full h-full object-cover opacity-80 group-hover:opacity-60 transition-opacity" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><MapPin className="w-12 h-12 text-white/30" /></div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="bg-white/20 backdrop-blur-sm rounded-full p-3"><Image className="w-6 h-6 text-white" /></div>
                      </div>
                      {/* Logo overlay */}
                      {branding.logo_url && <img src={branding.logo_url} alt="Logo" className="absolute top-5 left-6 h-10 object-contain drop-shadow-lg" />}
                    </div>
                    {/* Cover Content */}
                    <div className="px-10 py-8 -mt-16 relative z-10">
                      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-8 border border-slate-100 dark:border-slate-700">
                        <input className="text-2xl md:text-3xl font-bold w-full bg-transparent outline-none border-none mb-2" style={{ color: primary }}
                          value={page.title || ''} onChange={e => updatePage(selectedPage, 'title', e.target.value)} placeholder="Itinerary Title" data-testid="cover-title-input" />
                        <input className="text-base text-orange-500 font-medium w-full bg-transparent outline-none mb-4"
                          value={page.subtitle || ''} onChange={e => updatePage(selectedPage, 'subtitle', e.target.value)} placeholder="Duration (e.g., 5 Nights / 6 Days)" />
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                            <Calendar className="w-4 h-4" />
                            <input className="bg-transparent outline-none flex-1" value={page.dates || ''} onChange={e => updatePage(selectedPage, 'dates', e.target.value)} placeholder="Travel dates" />
                          </div>
                          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                            <Users className="w-4 h-4" />
                            <input className="bg-transparent outline-none flex-1" value={page.guests || ''} onChange={e => updatePage(selectedPage, 'guests', e.target.value)} placeholder="Guests" />
                          </div>
                          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400 col-span-2">
                            <Star className="w-4 h-4" />
                            <input className="bg-transparent outline-none flex-1 font-medium" value={page.client_name || ''} onChange={e => updatePage(selectedPage, 'client_name', e.target.value)} placeholder="Prepared for: Client Name" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── DAY PAGE ──────────────── */}
                {page.type === 'day' && (
                  <div className="p-0">
                    {/* Day Hero */}
                    <div className="relative h-44 bg-gradient-to-br from-sky-800 to-blue-900 overflow-hidden cursor-pointer group" onClick={() => openImageSearch(selectedPage, page.title || 'travel')}>
                      {page.image_url ? (
                        <img src={page.image_url} alt="" className="w-full h-full object-cover opacity-70 group-hover:opacity-50 transition-opacity" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><Image className="w-8 h-8 text-white/30" /></div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute bottom-4 left-6 right-6">
                        <div className="inline-block px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider mb-2 text-white" style={{ background: primary + 'cc' }}>
                          Day {page.day_number || ''}
                        </div>
                        <input className="text-xl font-bold w-full bg-transparent outline-none text-white placeholder-white/50 drop-shadow"
                          value={page.title || ''} onChange={e => updatePage(selectedPage, 'title', e.target.value)} placeholder="Day Title" data-testid="day-title-input" />
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="bg-white/20 backdrop-blur-sm rounded-full p-2.5"><Image className="w-5 h-5 text-white" /></div>
                      </div>
                    </div>
                    {/* Day Content */}
                    <div className="px-8 py-6 space-y-5">
                      <textarea className="w-full bg-transparent outline-none resize-none text-sm leading-relaxed min-h-[100px] text-slate-700 dark:text-slate-300"
                        value={page.description || ''} onChange={e => updatePage(selectedPage, 'description', e.target.value)} placeholder="Describe the day's journey..." rows={5} />
                      {/* Activities */}
                      <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4">
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Activities</p>
                        {(page.activities || []).map((act, ai) => (
                          <div key={ai} className="flex items-center gap-2 mb-2 group">
                            <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: primary }} />
                            <input className="flex-1 text-sm bg-transparent outline-none" value={act} onChange={e => updatePageNested(selectedPage, 'activities', ai, e.target.value)} placeholder="Activity..." />
                            <button className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => updatePage(selectedPage, 'activities', (page.activities || []).filter((_, j) => j !== ai))}><Trash2 className="w-3 h-3" /></button>
                          </div>
                        ))}
                        <Button variant="ghost" size="sm" className="text-xs gap-1 h-7 text-slate-500" onClick={() => updatePage(selectedPage, 'activities', [...(page.activities || []), ''])}><Plus className="w-3 h-3" /> Add Activity</Button>
                      </div>
                      {/* Hotel & Meals */}
                      <div className="flex gap-4">
                        <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg bg-violet-50 dark:bg-violet-900/20 text-sm">
                          <Hotel className="w-4 h-4 text-violet-500" />
                          <input className="bg-transparent outline-none flex-1 text-sm" value={page.hotel || ''} onChange={e => updatePage(selectedPage, 'hotel', e.target.value)} placeholder="Hotel name" />
                        </div>
                        <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-sm">
                          <Utensils className="w-4 h-4 text-amber-500" />
                          <input className="bg-transparent outline-none flex-1 text-sm" value={page.meals || ''} onChange={e => updatePage(selectedPage, 'meals', e.target.value)} placeholder="Meals included" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── HOTELS ────────────────── */}
                {page.type === 'hotels' && (
                  <div className="p-8">
                    <input className="text-xl font-bold w-full bg-transparent outline-none mb-5" style={{ color: primary }} value={page.title || ''} onChange={e => updatePage(selectedPage, 'title', e.target.value)} />
                    <div className="space-y-4">
                      {(page.hotels || []).map((h, hi) => (
                        <div key={hi} className="flex gap-4 p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30 group">
                          {/* Hotel Image */}
                          <div className="w-40 h-28 rounded-lg overflow-hidden flex-shrink-0 cursor-pointer relative" onClick={() => openHotelImageSearch(hi)}>
                            {h.image_url ? (
                              <>
                                <img src={h.image_url} alt="" className="w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                  <Image className="w-5 h-5 text-white" />
                                </div>
                              </>
                            ) : (
                              <div className="w-full h-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                                <div className="text-center">
                                  <Image className="w-5 h-5 text-slate-400 mx-auto mb-1" />
                                  <span className="text-[9px] text-slate-400">Add Photo</span>
                                </div>
                              </div>
                            )}
                          </div>
                          {/* Hotel Details */}
                          <div className="flex-1 space-y-2">
                            <input className="text-sm font-bold bg-transparent outline-none w-full" style={{ color: primary }}
                              value={h.hotel_name || h.name || ''} onChange={e => { const arr = [...(page.hotels||[])]; arr[hi] = {...arr[hi], hotel_name: e.target.value}; updatePage(selectedPage, 'hotels', arr); }} placeholder="Hotel name" />
                            <input className="text-xs text-muted-foreground bg-transparent outline-none w-full"
                              value={h.place || h.city || ''} onChange={e => { const arr = [...(page.hotels||[])]; arr[hi] = {...arr[hi], place: e.target.value}; updatePage(selectedPage, 'hotels', arr); }} placeholder="City" />
                            <div className="flex gap-3">
                              <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-violet-50 dark:bg-violet-900/20 text-xs">
                                <Hotel className="w-3 h-3 text-violet-500" />
                                <input className="bg-transparent outline-none w-20 text-xs" value={h.room_type || ''} onChange={e => { const arr = [...(page.hotels||[])]; arr[hi] = {...arr[hi], room_type: e.target.value}; updatePage(selectedPage, 'hotels', arr); }} placeholder="Room type" />
                              </div>
                              <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-amber-50 dark:bg-amber-900/20 text-xs">
                                <Utensils className="w-3 h-3 text-amber-500" />
                                <input className="bg-transparent outline-none w-20 text-xs" value={h.meal_plan || ''} onChange={e => { const arr = [...(page.hotels||[])]; arr[hi] = {...arr[hi], meal_plan: e.target.value}; updatePage(selectedPage, 'hotels', arr); }} placeholder="Meal plan" />
                              </div>
                              <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-sky-50 dark:bg-sky-900/20 text-xs">
                                <Calendar className="w-3 h-3 text-sky-500" />
                                <input className="bg-transparent outline-none w-32 text-xs" value={h.date || ''} onChange={e => { const arr = [...(page.hotels||[])]; arr[hi] = {...arr[hi], date: e.target.value}; updatePage(selectedPage, 'hotels', arr); }} placeholder="Check-in — Check-out" />
                              </div>
                            </div>
                          </div>
                          <button className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 self-start mt-1" onClick={() => updatePage(selectedPage, 'hotels', (page.hotels||[]).filter((_,j) => j !== hi))}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <Button variant="ghost" size="sm" className="text-xs gap-1" onClick={() => updatePage(selectedPage, 'hotels', [...(page.hotels||[]), {hotel_name: '', place: '', room_type: '', meal_plan: '', date: '', image_url: null}])}><Plus className="w-3 h-3" /> Add Hotel</Button>
                    </div>
                  </div>
                )}

                {/* ── INCLUSIONS/EXCLUSIONS ───── */}
                {page.type === 'inclusions' && (
                  <div className="p-8 space-y-6">
                    <input className="text-xl font-bold w-full bg-transparent outline-none" style={{ color: primary }} value={page.title || ''} onChange={e => updatePage(selectedPage, 'title', e.target.value)} />
                    <div className="grid grid-cols-2 gap-6">
                      <div className="bg-emerald-50/50 dark:bg-emerald-900/10 rounded-xl p-5 border border-emerald-100 dark:border-emerald-800/30">
                        <h3 className="text-sm font-bold text-emerald-600 mb-3 flex items-center gap-1.5"><Check className="w-4 h-4" /> Inclusions</h3>
                        {(page.inclusions || []).map((item, i) => (
                          <div key={i} className="flex items-center gap-2 mb-2 group">
                            <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                            <input className="flex-1 text-sm bg-transparent outline-none" value={item} onChange={e => updatePageNested(selectedPage, 'inclusions', i, e.target.value)} />
                            <button className="text-red-400 opacity-0 group-hover:opacity-100" onClick={() => updatePage(selectedPage, 'inclusions', (page.inclusions||[]).filter((_,j)=>j!==i))}><Trash2 className="w-3 h-3" /></button>
                          </div>
                        ))}
                        <Button variant="ghost" size="sm" className="text-xs gap-1 h-6 text-emerald-600" onClick={() => updatePage(selectedPage, 'inclusions', [...(page.inclusions||[]), ''])}><Plus className="w-3 h-3" /> Add</Button>
                      </div>
                      <div className="bg-red-50/50 dark:bg-red-900/10 rounded-xl p-5 border border-red-100 dark:border-red-800/30">
                        <h3 className="text-sm font-bold text-red-600 mb-3 flex items-center gap-1.5"><X className="w-4 h-4" /> Exclusions</h3>
                        {(page.exclusions || []).map((item, i) => (
                          <div key={i} className="flex items-center gap-2 mb-2 group">
                            <X className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                            <input className="flex-1 text-sm bg-transparent outline-none" value={item} onChange={e => updatePageNested(selectedPage, 'exclusions', i, e.target.value)} />
                            <button className="text-red-400 opacity-0 group-hover:opacity-100" onClick={() => updatePage(selectedPage, 'exclusions', (page.exclusions||[]).filter((_,j)=>j!==i))}><Trash2 className="w-3 h-3" /></button>
                          </div>
                        ))}
                        <Button variant="ghost" size="sm" className="text-xs gap-1 h-6 text-red-600" onClick={() => updatePage(selectedPage, 'exclusions', [...(page.exclusions||[]), ''])}><Plus className="w-3 h-3" /> Add</Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── PRICING ──────────────── */}
                {page.type === 'pricing' && (
                  <div className="p-8 space-y-4">
                    <input className="text-xl font-bold w-full bg-transparent outline-none" style={{ color: primary }} value={page.title || ''} onChange={e => updatePage(selectedPage, 'title', e.target.value)} />
                    <div className="bg-orange-50/50 dark:bg-orange-900/10 rounded-xl p-5 border border-orange-100 dark:border-orange-800/30">
                      <textarea className="w-full bg-transparent outline-none resize-none text-sm min-h-[180px] whitespace-pre-wrap"
                        value={page.costing || ''} onChange={e => updatePage(selectedPage, 'costing', e.target.value)} placeholder="Enter pricing details, package cost breakdown..." rows={10} />
                    </div>
                  </div>
                )}

                {/* ── TERMS ────────────────── */}
                {page.type === 'terms' && (
                  <div className="p-8 space-y-4">
                    <input className="text-xl font-bold w-full bg-transparent outline-none" style={{ color: primary }} value={page.title || ''} onChange={e => updatePage(selectedPage, 'title', e.target.value)} />
                    <div className="space-y-2">
                      {(page.terms || []).map((t, i) => (
                        <div key={i} className="flex items-start gap-2 group">
                          <span className="text-xs font-bold text-slate-400 mt-0.5 min-w-[20px]">{i+1}.</span>
                          <input className="flex-1 text-sm bg-transparent outline-none" value={t} onChange={e => updatePageNested(selectedPage, 'terms', i, e.target.value)} />
                          <button className="text-red-400 opacity-0 group-hover:opacity-100" onClick={() => updatePage(selectedPage, 'terms', (page.terms||[]).filter((_,j)=>j!==i))}><Trash2 className="w-3 h-3" /></button>
                        </div>
                      ))}
                      <Button variant="ghost" size="sm" className="text-xs gap-1 h-6" onClick={() => updatePage(selectedPage, 'terms', [...(page.terms||[]), ''])}><Plus className="w-3 h-3" /> Add Term</Button>
                    </div>
                    {(page.notes !== undefined) && (
                      <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-4 mt-4">
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Important Notes</p>
                        <textarea className="w-full bg-transparent outline-none resize-none text-sm min-h-[60px]"
                          value={typeof page.notes === 'string' ? page.notes : (page.notes || []).join('\n')}
                          onChange={e => updatePage(selectedPage, 'notes', e.target.value)} placeholder="Additional notes..." rows={3} />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* AI Agent Panel */}
      {showAgent && (
        <div className="fixed bottom-4 right-4 w-96 h-[500px] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 flex flex-col z-50 overflow-hidden" data-testid="agent-panel">
          <div className="flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-violet-600 to-indigo-600 text-white">
            <Bot className="w-5 h-5" />
            <span className="font-heading font-bold text-sm flex-1">AI Assistant</span>
            <span className="text-[10px] opacity-70">Page {selectedPage + 1}: {page?.title?.substring(0, 20)}</span>
            <button onClick={() => setShowAgent(false)} className="hover:bg-white/20 rounded p-0.5"><X className="w-4 h-4" /></button>
          </div>
          <ScrollArea className="flex-1 p-3">
            <div className="space-y-3">
              {agentMessages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role === 'status' ? (
                    <div className="flex items-center gap-2 px-3 py-1.5 text-xs text-violet-500 animate-pulse">
                      <Loader2 className="w-3 h-3 animate-spin" /> {msg.text}
                    </div>
                  ) : (
                    <div className={`max-w-[85%] px-3 py-2 rounded-xl text-sm whitespace-pre-wrap ${
                      msg.role === 'user'
                        ? 'bg-blue-600 text-white rounded-br-sm'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-bl-sm'
                    }`}>
                      {msg.text}
                    </div>
                  )}
                </div>
              ))}
              {agentProcessing && (
                <div className="flex justify-start">
                  <div className="bg-slate-100 dark:bg-slate-800 px-4 py-2 rounded-xl rounded-bl-sm">
                    <div className="flex gap-1"><span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay:'0ms'}} /><span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay:'150ms'}} /><span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{animationDelay:'300ms'}} /></div>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
          </ScrollArea>
          <div className="p-3 border-t border-slate-200 dark:border-slate-700">
            <div className="flex gap-2">
              <input value={agentInput} onChange={e => setAgentInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendAgentMessage()}
                placeholder="Type your change request..."
                className="flex-1 text-sm px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent outline-none focus:border-violet-400" data-testid="agent-input" />
              <Button size="sm" onClick={sendAgentMessage} disabled={agentProcessing || !agentInput.trim()}
                className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white px-3" data-testid="agent-send">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Agent Button */}
      {!showAgent && (
        <button onClick={() => setShowAgent(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-full shadow-lg shadow-violet-500/30 flex items-center justify-center hover:scale-105 transition-transform z-50"
          data-testid="agent-fab">
          <MessageSquare className="w-6 h-6" />
        </button>
      )}

      {/* Image Search Dialog */}
      <Dialog open={showImageSearch} onOpenChange={setShowImageSearch}>
        <DialogContent className="max-w-2xl max-h-[85vh]">
          <DialogHeader><DialogTitle className="font-heading">Find the Perfect Image</DialogTitle></DialogHeader>
          <div className="flex gap-2 mb-3">
            <Input placeholder="Search destinations, landmarks, beaches..." value={imageQuery} onChange={e => setImageQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && searchImages()} className="flex-1" data-testid="image-search-input" />
            <Button onClick={searchImages} disabled={searchingImages} className="gap-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
              {searchingImages ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Search
            </Button>
            <Button variant="outline" onClick={generateAIImage} disabled={aiGenerating} className="gap-1.5" data-testid="ai-image-btn">
              {aiGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} AI
            </Button>
          </div>
          <ScrollArea className="h-[420px]">
            <div className="grid grid-cols-3 gap-2.5">
              {imageResults.map(img => (
                <div key={img.id} className="relative group cursor-pointer rounded-xl overflow-hidden aspect-[4/3] shadow-sm hover:shadow-md transition-shadow" onClick={() => selectImage(img)} data-testid={`image-result-${img.id}`}>
                  <img src={img.url_small || img.url_regular} alt={img.alt} className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="bg-white rounded-full p-2"><Check className="w-5 h-5 text-emerald-600" /></div>
                  </div>
                  <div className="absolute bottom-1.5 left-1.5 right-1.5 flex justify-between items-end">
                    <span className="text-[9px] px-1.5 py-0.5 bg-black/60 text-white rounded-full capitalize">{img.source}</span>
                    {img.photographer && <span className="text-[8px] text-white/80 drop-shadow truncate max-w-[100px]">{img.photographer}</span>}
                  </div>
                </div>
              ))}
            </div>
            {imageResults.length === 0 && !searchingImages && (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Image className="w-10 h-10 mb-3 opacity-30" />
                <p className="text-sm">Search for images or generate with AI</p>
                <p className="text-xs mt-1">Powered by Unsplash + Pixabay</p>
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Document Upload Dialog */}
      <Dialog open={showUploadDoc} onOpenChange={setShowUploadDoc}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Import Document</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Upload a PDF, Word, Excel, or text file with trip details. AI will extract structured data.</p>
          <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-border rounded-xl">
            <Upload className="w-10 h-10 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground mb-3">PDF, DOCX, XLSX, TXT</p>
            <Button onClick={() => document.getElementById('doc-upload-input')?.click()} disabled={uploadingDoc} className="gap-2 bg-voyage-blue text-white">
              {uploadingDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Choose File
            </Button>
            <input id="doc-upload-input" type="file" accept=".pdf,.docx,.xlsx,.xls,.txt" onChange={handleDocUpload} className="hidden" />
          </div>
        </DialogContent>
      </Dialog>

      {/* Revision History Dialog */}
      <Dialog open={showRevisions} onOpenChange={setShowRevisions}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><History className="w-5 h-5" /> Revision History</DialogTitle></DialogHeader>
          <ScrollArea className="h-[350px]">
            {revisions.length === 0 ? (
              <p className="text-center text-muted-foreground py-8 text-sm">No revisions yet. Changes are saved automatically.</p>
            ) : (
              <div className="space-y-2">
                {revisions.map((rev, i) => (
                  <div key={rev.id} className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/50">
                    <div>
                      <p className="text-sm font-medium">{i === 0 ? 'Latest' : `Version ${revisions.length - i}`}</p>
                      <p className="text-xs text-muted-foreground">{new Date(rev.created_at).toLocaleString()}</p>
                    </div>
                    {i > 0 && (
                      <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={() => restoreRevision(rev.id)} data-testid={`restore-rev-${rev.id}`}>
                        <RotateCcw className="w-3 h-3" /> Restore
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Email Itinerary Dialog */}
      <Dialog open={showEmail} onOpenChange={setShowEmail}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Mail className="w-5 h-5 text-emerald-600" /> Email Itinerary to Client</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Recipient Email *</label>
              <Input value={emailData.to_email} onChange={e => setEmailData({...emailData, to_email: e.target.value})} placeholder="client@email.com" className="mt-1" data-testid="email-to-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Subject (optional)</label>
              <Input value={emailData.subject} onChange={e => setEmailData({...emailData, subject: e.target.value})} placeholder={`${itinerary?.title || 'Your Itinerary'}`} className="mt-1" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Message (optional)</label>
              <textarea className="w-full mt-1 p-2.5 text-sm border border-border rounded-lg bg-background outline-none focus:ring-1 focus:ring-primary resize-none min-h-[80px]"
                value={emailData.message} onChange={e => setEmailData({...emailData, message: e.target.value})}
                placeholder={`Dear ${lead?.client_name || 'Guest'},\n\nPlease find attached your travel itinerary.\n\nBest regards`} rows={4} />
            </div>
            <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 text-xs text-muted-foreground">
              The itinerary PDF (with images) will be automatically attached to this email.
            </div>
            <Button onClick={handleSendEmail} disabled={sendingEmail} className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="send-email-btn">
              {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sendingEmail ? 'Sending...' : 'Send Email with PDF'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
